import { defineStore } from 'pinia';
import type { FindGenericRequest } from '../../shared/models/requests/findGeneric.request';
import {
  FindGenericResponse,
  GenericSearchResult,
  SearchFilterBucket,
  SearchMediaResult,
} from '../../shared/models/responses/search/findGeneric.response';
import { searchService } from '../../shared/services/api/search.service';
import { docTypes } from '../../shared/enums/docTypes';
import { computed, ref } from 'vue';
import { portalRoles } from '../../shared/enums/portalRoles';
import { groupTypes as GroupTypeEnum } from '../../shared/enums/groupTypes';
import { groupMembershipRoles } from '../../shared/enums/groupMembershipRoles';

export interface GlobalSearchOptions {
  institutionProfileIds?: number[];
  activeChildrenInstitutionProfileIds?: number[];
  limit?: number;
  offset?: number;
  query?: string;
  groupId?: number;
}

export const useGlobalSearchStore = defineStore('globalSearch', () => {
  const query = ref<string>('');
  const isLoading = ref<boolean>(false);
  const response = ref<FindGenericResponse | null>(null);
  const docType = ref<docTypes>(docTypes.PROFILE);
  const roles = ref<portalRoles[]>([]);
  const groupTypes = ref<GroupTypeEnum[]>([]);
  const mediaSortOrder = ref<'asc' | 'desc'>('desc');
  const error = ref<Error>(null);

  const searchedQuery = computed<string>(() => response.value?.query || '');
  const hasQuery = computed<boolean>(() => !!query.value.trim());
  const isEmpty = computed<boolean>(() => !query.value.trim());
  const results = computed<GenericSearchResult[]>(() => response.value?.results ?? []);
  const mediaResults = computed<SearchMediaResult[]>(() => response.value?.mediaResults ?? []);
  const totalCount = computed<number>(() => response.value?.totalSize ?? 0);
  const hasMore = computed<boolean>(() => response.value?.totalSize > results.value.length + mediaResults.value.length);
  const filters = computed<SearchFilterBucket[]>(() => {
    const orderedDocTypes = [
      docTypes.PROFILE,
      docTypes.GROUP,
      docTypes.INTERNAL_SECURE_FILE,
      docTypes.EXTERNAL_SECURE_FILE,
      docTypes.IMPORTANT_FILE,
      docTypes.THREAD_MESSAGE,
      docTypes.EVENT,
      docTypes.POST,
      docTypes.MEDIA,
    ];
    const priority = Object.fromEntries(orderedDocTypes.map((type, index) => [type, index]));
    const docTypeCount = response.value?.docTypeCount ?? [];
    return docTypeCount.sort((a, b) => priority[a.key] - priority[b.key]);
  });
  const profileFilters = computed<SearchFilterBucket[]>(() => response.value?.profileTypeCount || []);
  const groupFilters = computed<SearchFilterBucket[]>(() => response.value?.groupTypeCount || []);

  const setResponse = (newResponse: FindGenericResponse) => {
    response.value = { ...newResponse };
  };

  const setQuery = (value: string) => {
    query.value = value;
  };

  const clearQuery = () => {
    query.value = '';
  };

  const setDocType = (value: docTypes) => {
    docType.value = value;
  };

  const setRoles = (value: portalRoles[]) => {
    roles.value = value;
  };

  const clearResults = () => {
    if (response.value) {
      response.value.results = [];
      response.value.mediaResults = [];
    }
  };

  const reset = () => {
    response.value = null;
    docType.value = docTypes.PROFILE;
    mediaSortOrder.value = 'desc';
  };

  const fetch = async (options: GlobalSearchOptions = {}) => {
    if (!query.value) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const payload: Pick<
        FindGenericRequest,
        | 'limit'
        | 'offset'
        | 'institutionProfileIds'
        | 'activeChildrenInstitutionProfileIds'
        | 'text'
        | 'roles'
        | 'groupTypes'
        | 'docType'
        | 'docTypeCount'
        | 'groupId'
      > = {
        limit: options.limit ?? 20,
        offset: options.offset ?? 0,
        institutionProfileIds: options.institutionProfileIds,
        activeChildrenInstitutionProfileIds: options.activeChildrenInstitutionProfileIds,
        text: query.value,
        roles: roles.value,
        groupTypes: groupTypes.value,
        docType: docType.value,
        docTypeCount: true,
        groupId: options.groupId,
      };

      const requestPayload = payload as FindGenericRequest;
      // Media results are sorted chronologically (newest first by default); the
      // sort dropdown lets the user flip to oldest first. Other doc types keep
      // the backend's relevance-based ordering.
      if (docType.value === docTypes.MEDIA) {
        requestPayload.sortOn = 'datetime';
        requestPayload.orderDirection = mediaSortOrder.value;
      }

      const newResponse = await searchService.findGeneric(requestPayload);
      isLoading.value = false;
      return newResponse;
    } catch (err) {
      error.value = err;
      throw err;
    }
  };

  const initializeSearch = async (
    options: Pick<GlobalSearchOptions, 'institutionProfileIds' | 'activeChildrenInstitutionProfileIds' | 'groupId'>
  ) => {
    clearResults();
    try {
      const newResponse = await fetch({ ...options, offset: 0 });
      setResponse(newResponse);
    } catch (error) {
      return;
    }
  };

  const searchMore = async (
    options: Pick<GlobalSearchOptions, 'institutionProfileIds' | 'activeChildrenInstitutionProfileIds' | 'groupId'>
  ) => {
    const newResponse = await fetch({ ...options, offset: results?.value.length + mediaResults?.value.length });
    appendResults(newResponse);
  };

  const appendResults = (newResponse: FindGenericResponse) => {
    const { results: newResults, mediaResults: newMediaResults, ...otherProps } = newResponse;
    Object.assign(response.value, otherProps);
    response.value.results.push(...newResults);
    response.value.mediaResults.push(...newMediaResults);
  };

  const updateResultMembershipRole = (groupId: string, newRole: groupMembershipRoles) => {
    const targetResult: GenericSearchResult | null = results.value.find(
      result => result.id === groupId && result.docType === docTypes.GROUP
    );
    if (targetResult && 'membershipRole' in targetResult) {
      targetResult.membershipRole = newRole;
      targetResult.currentUserCanAccessGroupDashboard = newRole === groupMembershipRoles.MEMBER;
    }
  };

  return {
    query,
    searchedQuery,
    docType,
    roles,
    groupTypes,
    mediaSortOrder,
    isLoading,
    response,
    hasQuery,
    isEmpty,
    filters,
    profileFilters,
    groupFilters,
    results,
    mediaResults,
    totalCount,
    hasMore,
    error,
    setQuery,
    clearQuery,
    setDocType,
    setRoles,
    clearResults,
    initializeSearch,
    searchMore,
    reset,
    updateResultMembershipRole,
  };
});
